import axios, { AxiosResponse } from "axios";
import config from "./config";
import { PickupAddressFormProps } from "../components/ui/PickupAddressDrawer";
import toast from "react-hot-toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClientAddressType } from "../hooks/usePickupAddress";
// import { ReturnsFormType } from "../components/features/returns/AddReturns";
import { Dayjs } from "dayjs";

const options = {
  timeout: 100000,
  headers: {
    "Content-Type": "application/json;charset=utf-8",
  },
};
export const api = axios.create({
  baseURL: config.APIDEV,
  ...options,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("hipship-token");
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    const errorCode = error?.response?.status;

    if (errorCode === 401) {
      localStorage.clear();
    } else if (errorCode === 500) {
      toast.error("Something went wrong!");
    }
    return Promise.reject(error);
  }
);

interface loginType {
  username: string;
  password: string;
}

// user login
export const userLogin = (loginData: loginType) => {
  return api.post("/bidsite/login/", loginData);
};

interface ShopifyLoginData {
  username: string;
  password: string;
  access_token: string;
  shop: string;
  channel: string;
}

export const shopifyLogin = (loginData: ShopifyLoginData) => {
  return api.post("/saleschannel/login_user/", loginData);
};

interface registerType {
  user: object;
  mobile: string;
  address: string;
  // email: string;
  // password: string;
  user_type: string;
  city: string;
  state: string;
  pincode: string;
  company_name: string;
  pan_number: string;
  business_type: string;
  shipment_volume: string;
}

//user register
export const UserRegister = (loginData: registerType) => {
  return api.post("/bidsite/register/", loginData);
};

interface ShopifyRegisterType {
  user: UserData;
  mobile: string;
  address: string;
  user_type: string;
  city: string;
  state: string;
  pincode: string;
  company_name: string;
  pan_number: string;
  business_type: string;
  shipment_volume: string;
  access_token: string;
  shop: string;
  channel: string;
}

interface UserData {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
}

export const UserShopifyRegister = (loginData: ShopifyRegisterType) => {
  return api.post("/saleschannel/signup/", loginData);
};

// 680694
export const GetStateByPincode = async (code: string) => {
  return api.get("/b2c/getstate/?pincode=" + code);
};

export const getUserPickupAddress = () => {
  return api.get("/b2c/pickup_address/");
};

export const getPincodeApi = (pincode: string) => {
  return api.get("/b2c/getstate/", {
    params: {
      pincode: pincode,
    },
  });
};

export const resetPassword = (data: {
  email: string;
  otp: string;
  new_password: string;
}) => {
  return api.post(
    `/bidsite/reset_password_otp/?email=${data.email}&otp=${data.otp}&new_password=${data.new_password}`
  );
};
export const forgotPassword = (data: { email: string }) => {
  return api.post(`/bidsite/forgot_password_otp/?email=${data.email}`);
};

export const storePickupAddress = (data: PickupAddressFormProps) => {
  return api.post("b2c/pickup_address/", data);
};

export const disputeShipment = (
  status: string,
  from_date: string,
  to_date: string
) => {
  return api.get("/b2c/dispute_shipment/", {
    params: {
      status,
      from_date,
      to_date,
    },
  });
};

export const disputeShipmentPost = (dispute_id: any, status: string) => {
  return api.post(
    `/b2c/dispute_shipment/?dispute_id=${dispute_id}&status=${status}`
  );
};

export function useGetPickupAddress() {
  const { isLoading, data, isError, error } = useQuery({
    queryKey: ["pickup-address"],
    queryFn: getUserPickupAddress,
  });

  let pickupAddress: ClientAddressType[] = [];

  if (!isLoading && !isError && data) {
    pickupAddress = data?.data as ClientAddressType[];
    pickupAddress.map((el, index) => (el.id = index + 1));
  }

  return { isLoading, error, pickupAddress };
}

export const useAddPickupAddress = () => {
  const queryClient = useQueryClient();

  const { isSuccess, mutate: storeAddress } = useMutation({
    mutationFn: (data: PickupAddressFormProps) => storePickupAddress(data),
    onSuccess: () => {
      toast.success("Pickup address added successfully");

      queryClient.invalidateQueries({
        queryKey: ["pickup-address"],
      });
    },
    onError: (err) => toast.error(err.message),
  });

  return { isSuccess, storeAddress };
};

interface companyTypes {
  company_name: string;
  company_url: string;
  company_email: string;
  company_logo_image: File | undefined;
}

export const addCompanyDetail = (data: companyTypes) => {
  return api.post("/bidsite/add_company_profile/", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const fetchCompanyDetail = () => {
  return api.get("/bidsite/add_company_profile/");
};

export const updateCompanyDetail = (data: companyTypes) => {
  return api.put("/bidsite/add_company_profile/", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const FetchReports = (
  startDate: Dayjs,
  endDate: Dayjs,
  reportType: string
) => {
  const fromDate = startDate.format("YYYY-MM-DD");
  const toDate = endDate.format("YYYY-MM-DD");

  return api.get(`/b2c/order_reports/`, {
    params: {
      from_date: fromDate,
      to_date: toDate,
      report_type: reportType,
    },
  });
};
export const FetchMISReports = (
  startDate: Dayjs,
  endDate: Dayjs,
  reportType: string
) => {
  const fromDate = startDate.format("YYYY-MM-DD");
  const toDate = endDate.format("YYYY-MM-DD");

  return api.get(`/b2c/mis_reports/`, {
    params: {
      from_date: fromDate,
      to_date: toDate,
      report_type: reportType,
    },
  });
};

interface PrintType {
  doc_type: string;
  awb_numbers?: any;
  shipment_ids?: any;
}

export const BulkPrintActionApi = (data: PrintType) => {
  return api.post("/b2c/bulk_print/", data, {
    responseType: "blob",
  });
};
export const getAllShipmentData = (
  page: number,
  startDate: Dayjs,
  endDate: Dayjs
) => {
  const start = startDate.format("YYYY-MM-DD");
  const end = endDate.format("YYYY-MM-DD");
  return api.get(
    `/b2c/billing/shipment_charges/?from_date=${start}&to_date=${end}&page=${page}`
  );
};
export const getAllCodRemittanceData = (
  page: number,
  startDate: Dayjs,
  endDate: Dayjs,
  cod_type: string
) => {
  const start = startDate.format("YYYY-MM-DD");
  const end = endDate.format("YYYY-MM-DD");
  return api.get(
    `/b2c/billing/cod_remittance/?from_date=${start}&to_date=${end}&page=${page}&cod_type=${cod_type}`
  );
};

export const getAllWalletHistoryData = (startDate: Dayjs, endDate: Dayjs) => {
  const start = startDate.format("YYYY-MM-DD");
  const end = endDate.format("YYYY-MM-DD");
  return api.get(
    `/payment/walletHistoryApiView/?from_date=${start}&to_date=${end}`
  );
};

export const getAllInvoiceData = (
  page: number,
  startDate: Dayjs,
  endDate: Dayjs
) => {
  const start = startDate.format("YYYY-MM-DD");
  const end = endDate.format("YYYY-MM-DD");
  return api.get(
    `/b2c/billing/invoice/?from_date=${start}&to_date=${end}&page=${page}`
  );
};
export const getAllPassbookData = (
  page: number,
  startDate: Dayjs,
  endDate: Dayjs
) => {
  const start = startDate.format("YYYY-MM-DD");
  const end = endDate.format("YYYY-MM-DD");
  return api.get(
    `/b2c/billing/passbook/?from_date=${start}&to_date=${end}&page=${page}`
  );
};

interface RateFormData {
  shipment_type: string;
  pickup_pincode: string;
  desti_pincode: string;
  invoice_value: string;
  cod: string;
  cod_amount: string;
  shipment_weight: string;
  shipment_length: string;
  shipment_width: string;
  shipment_height: string;
  is_return: boolean;
}

// user login
// export const getRateCalculator = (RateFormData: RateFormData) => {
//   return api.post("/b2b/rate_calculator/", RateFormData);
// };

export const getRateCalculator = (data: RateFormData) => {
  return api.post("/b2c/rate_calculator/", data);
};

// user roles apis
export const fetchAllUserRole = () => {
  return api.get("/bidsite/StaffApiView/");
};

export const fetchAllRoles = () => {
  return api.get("/bidsite/sideMenus/");
};

interface RoleFormData {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  user_type: string;
  modules: string[];
}

export const postUserRole = (data: RoleFormData) => {
  return api.post("/bidsite/StaffApiView/", data, {});
};

export const fetchSingleRoles = (user_id: any) => {
  return api.get(`/bidsite/DetailsStaffApiView/${user_id}`);
};

export const putSingleRoles = (user_id: any, data: any) => {
  return api.put(`/bidsite/DetailsStaffApiView/${user_id}`, data);
};

export const fetchAwbno = () => {
  return api.get(`/bidsite/fetch_awb_no/`);
};

export const fetchTicketCategory = () => {
  return api.get(`/bidsite/ticket_categorys/`);
};
interface AwbnoData {
  id: string;
  guid: string;
}

export interface TicketFormData {
  awb_no: string[];
  category: string;
  query_remark: string;
}

export const createTicket = (data: TicketFormData) => {
  return api.post("/bidsite/ticket/", data, {});
};

export const getAllTicket = () => {
  return api.get("/bidsite/ticket/");
};

export interface CloseTicketData {
  query_id: string;
  status: string;
}

export const CloseTicketByAdmin = (data: CloseTicketData) => {
  return api.put("/bidsite/ticket_update/", data, {});
};

export const getMenuSubMenu = () => {
  return api.get("/bidsite/sideMenus/");
};

export interface PermissionData {
  staff_id: string;
}

export const getSubUserRoles = (data: PermissionData) => {
  return api.post("/bidsite/check_user_roles/", data, {});
};
